// src/components/CategoryManagement.js

import React, { useState, useEffect, useRef } from "react";
import "../style/CategoryManagement.css";
import { db } from "../firebase-config";
import {
  doc,
  setDoc,
  collection,
  getDocs,
  deleteDoc,
  serverTimestamp,
  query,
  orderBy,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";

const CategoryManagement = () => {
  const today = new Date().toLocaleDateString();
  const navigate = useNavigate();
  const [isAdding, setIsAdding] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [categories, setCategories] = useState([]);
  const [statusMessage, setStatusMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [categoryProducts, setCategoryProducts] = useState({});
  const timeoutRef = useRef(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setIsLoading(true);
    try {
      const q = query(
        collection(db, "categories"),
        orderBy("createdAt", "desc")
      );
      const querySnapshot = await getDocs(q);
      const categoriesList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCategories(categoriesList);

      // Fetch product counts for each category
      await fetchProductCounts(categoriesList);
    } catch (error) {
      console.error("Error fetching categories: ", error);
      setStatusMessage("Failed to fetch categories.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProductCounts = async (categoriesList) => {
    const counts = {};
    for (const category of categoriesList) {
      try {
        const itemsCollection = collection(
          db,
          "products",
          category.id,
          "items"
        );
        const itemsSnapshot = await getDocs(itemsCollection);
        counts[category.id] = itemsSnapshot.size;
      } catch (error) {
        console.error(`Error fetching products for ${category.id}:`, error);
        counts[category.id] = 0;
      }
    }
    setCategoryProducts(counts);
  };

  const handleAddCategoryClick = () => {
    setIsAdding(true);
  };

  const handleDoneClick = async () => {
    if (categoryName.trim()) {
      setIsLoading(true);
      setStatusMessage("");
      try {
        const categoryDocRef = doc(db, "categories", categoryName);
        await setDoc(categoryDocRef, {
          name: categoryName,
          createdAt: serverTimestamp(),
        });

        await fetchCategories();

        setIsAdding(false);
        setCategoryName("");

        setStatusMessage("Category created successfully.");

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
          setStatusMessage("");
        }, 3000);
      } catch (error) {
        console.error("Error adding category: ", error);
        setStatusMessage("Failed to create category. Please try again.");

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
          setStatusMessage("");
        }, 3000);
      } finally {
        setIsLoading(false);
      }
    } else {
      setStatusMessage("Category name cannot be empty.");

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setStatusMessage("");
      }, 3000);
    }
  };

  const handleCancelClick = () => {
    setIsAdding(false);
    setCategoryName("");
    setStatusMessage("");
  };

  const handleDeleteCategory = async (categoryId) => {
    const productCount = categoryProducts[categoryId] || 0;

    if (productCount > 0) {
      alert(
        `Cannot delete category "${categoryId}" because it contains ${productCount} product(s). Please delete all products first.`
      );
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to delete the category "${categoryId}"?`
      )
    ) {
      setIsLoading(true);
      try {
        await deleteDoc(doc(db, "categories", categoryId));
        await fetchCategories();
        setStatusMessage("Category deleted successfully.");

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
          setStatusMessage("");
        }, 3000);
      } catch (error) {
        console.error("Error deleting category: ", error);
        setStatusMessage("Failed to delete category. Please try again.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleViewProducts = (categoryId) => {
    // Navigate to the CategoryProducts component
    navigate("/category-products", { state: { categoryId } });
  };

  return (
    <div className="category-management-container">
      <div className="header">
        <div className="date">{today}</div>
      </div>
      <div className="title-section">
        <div className="category-management-title">Category Management</div>
        {!isAdding && (
          <div className="add-category-container">
            <div className="add-category-box" onClick={handleAddCategoryClick}>
              ADD CATEGORY
            </div>
          </div>
        )}
      </div>
      {isAdding && (
        <div className="add-category-form">
          <div className="add-category-title">Add Category</div>
          <div className="form-group-unique">
            <label>Name:</label>
            <input
              type="text"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              placeholder="Enter category name"
            />
          </div>
          <div className="button-group">
            <button
              className="done-button"
              onClick={handleDoneClick}
              disabled={isLoading}
            >
              {isLoading ? "Creating..." : "Done"}
            </button>
            <button
              className="cancel-button"
              onClick={handleCancelClick}
              disabled={isLoading}
            >
              Cancel
            </button>
          </div>
          {statusMessage && (
            <div className={`status-message ${isLoading ? "loading" : ""}`}>
              {statusMessage}
            </div>
          )}
        </div>
      )}
      <div className="categories-list">
        {isLoading ? (
          <div style={{ textAlign: "center", width: "100%" }}>
            Loading categories...
          </div>
        ) : categories.length === 0 ? (
          <div style={{ textAlign: "center", width: "100%" }}>
            No categories yet. Click "ADD CATEGORY" to create one.
          </div>
        ) : (
          categories.map((category) => (
            <div key={category.id} className="category-box">
              <div className="category-header">
                <span className="category-name">{category.id}</span>
                {category.createdAt && (
                  <span className="category-date">
                    {category.createdAt.toDate
                      ? category.createdAt.toDate().toLocaleDateString()
                      : new Date().toLocaleDateString()}
                  </span>
                )}
              </div>
              <div
                style={{ margin: "10px 0", fontSize: "14px", color: "#666" }}
              >
                {categoryProducts[category.id] !== undefined
                  ? `${categoryProducts[category.id]} product${
                      categoryProducts[category.id] !== 1 ? "s" : ""
                    }`
                  : "Loading..."}
              </div>
              <div className="category-actions">
                <button
                  className="action-button view-products-button"
                  onClick={() => handleViewProducts(category.id)}
                >
                  View Products
                </button>
                <button
                  className="action-button delete-category-button"
                  onClick={() => handleDeleteCategory(category.id)}
                  disabled={isLoading}
                >
                  Delete Category
                </button>
              </div>
            </div>
          ))
        )}
      </div>
      {statusMessage && !isAdding && (
        <div className="status-message">{statusMessage}</div>
      )}
    </div>
  );
};

export default CategoryManagement;
